import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  CalendarBlank,
  CaretLeft,
  CaretRight,
  MagnifyingGlass,
  Wrench,
} from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { gmao } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

const TYPE_META = {
  installation: { label: "Installation", shortLabel: "Installation" },
  preventive: { label: "Maintenance préventive", shortLabel: "Maintenance" },
  corrective: { label: "Panne / Réparation", shortLabel: "Panne / Réparation" },
  replacement: { label: "Remplacement", shortLabel: "Remplacement" },
  inspection: { label: "Autre", shortLabel: "Autre" },
  other: { label: "Autre", shortLabel: "Autre" },
};

const STATUS_META = {
  planned: {
    label: "À préparer",
    className:
      "border-[var(--do-border)] bg-[var(--do-bg-2)] text-[var(--do-ink)]",
  },
  confirmed: {
    label: "À préparer",
    className:
      "border-[var(--do-info)] bg-[var(--do-info-soft)] text-[var(--do-ink)]",
  },
  in_progress: {
    label: "En cours",
    className: "border-[var(--do-warning)] bg-[var(--do-warning-soft)] text-[var(--do-ink)]",
  },
  completed: {
    label: "Terminé",
    className: "border-[var(--do-success)] bg-[var(--do-success-soft)] text-[var(--do-ink)]",
  },
  cancelled: {
    label: "Annulé",
    className: "border-[var(--do-border)] bg-[var(--do-bg-2)] text-[var(--do-ink)]",
  },
};

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function startOfDay(value) {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
}

function startOfWeek(value) {
  const date = startOfDay(value);
  const day = date.getDay();
  date.setDate(date.getDate() + (day === 0 ? -6 : 1 - day));
  return date;
}

function addDays(value, amount) {
  const date = new Date(value);
  date.setDate(date.getDate() + amount);
  return date;
}

function dateKey(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatShortDate(value) {
  return new Date(value).toLocaleDateString("fr-FR", {
    weekday: "short",
    day: "2-digit",
    month: "short",
  });
}

function formatTime(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatWeekRange(weekStart) {
  const weekEnd = addDays(weekStart, 6);
  const start = weekStart.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "short",
  });
  const end = weekEnd.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  return `${start} — ${end}`;
}

function typeMeta(type) {
  return TYPE_META[type] || TYPE_META.other;
}

function statusMeta(status) {
  return STATUS_META[status] || {
    label: status || "—",
    className:
      "border-[var(--do-border)] bg-[var(--do-bg-2)] text-[var(--do-ink)]",
  };
}

function interventionDestination(item, user) {
  if (
    item.type === "installation" &&
    item.installationId &&
    item.status !== "cancelled" &&
    user?.role !== "technician"
  ) {
    return `/app/gmao/installations/${encodeURIComponent(item.installationId)}`;
  }

  return `/app/gmao/interventions/${encodeURIComponent(item.id)}`;
}

function PlanningCard({ item, onOpen, onRemove, canRemove }) {
  const type = typeMeta(item.type);
  const status = statusMeta(item.status);

  return (
    <div
      className="block w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-3 text-left transition hover:border-[var(--do-accent)] hover:shadow-md"
      data-testid={`gmao-planning-item-${item.id}`}
    >
      <button type="button" className="w-full text-left" onClick={() => onOpen(item)}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] font-heading font-black uppercase tracking-wide text-[var(--do-accent)]">
            {type.shortLabel}
          </div>
          <div className="mt-1 truncate font-heading text-xs font-black text-[var(--do-ink)]">
            {item.machineName || "Machine"}
          </div>
        </div>
        <span className="shrink-0 font-heading text-xs font-black text-[var(--do-accent)]">
          {item.scheduledDate && !item.scheduledStart ? "Sans horaire" : formatTime(item.scheduledStart)}
        </span>
      </div>

      <div className="mt-1 truncate text-xs text-[var(--do-muted)]">
        {item.clientName || item.siteName || "Client non renseigné"}
      </div>

      <div className="mt-2 flex items-center justify-between gap-2">
        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-heading font-bold ${status.className}`}>
          {status.label}
        </span>
        <span className="max-w-[110px] truncate text-[10px] text-[var(--do-muted)]">
          {item.assigneeName || item.technicianName || "Non affecté"}
        </span>
      </div>
      </button>
      {canRemove && item.status === "cancelled" && (
        <button type="button" className="mt-2 text-xs font-bold text-[var(--do-danger)] underline" onClick={() => onRemove(item)}>
          Supprimer du planning
        </button>
      )}
    </div>
  );
}

export default function GmaoPlanningV2() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [interventions, setInterventions] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [technicianFilter, setTechnicianFilter] = useState("all");
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const timezoneName =
        Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Paris";

      const [interventionData, dashboardData] = await Promise.all([
        gmao.listInterventions({ limit: 500 }),
        gmao.getDashboard({ timezone_name: timezoneName }),
      ]);

      setInterventions(safeArray(interventionData));
      setDashboard(dashboardData || null);
    } catch (error) {
      toast.error(
        formatApiError(error?.response?.data?.detail) ||
          "Impossible de charger le planning GMAO."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, index) => addDays(weekStart, index)),
    [weekStart]
  );

  const weekEnd = useMemo(() => addDays(weekStart, 7), [weekStart]);

  const weekItems = useMemo(
    () =>
      interventions.filter((item) => {
        if (item.planningHiddenAt) return false;
        const value = item.scheduledDate || item.scheduledStart;
        if (!value) return false;
        const start = new Date(value.length === 10 ? `${value}T12:00:00` : value);
        if (Number.isNaN(start.getTime())) return false;
        return start >= weekStart && start < weekEnd;
      }),
    [interventions, weekEnd, weekStart]
  );

  const technicians = useMemo(() => {
    const byId = new Map();

    safeArray(dashboard?.technicianWorkload).forEach((item) => {
      if (item.technicianId) {
        byId.set(item.technicianId, {
          id: item.technicianId,
          name: item.technicianName || "Technicien",
        });
      }
    });

    weekItems.forEach((item) => {
      const id = item.assignedUserId || item.assignedTechnicianId;
      if (id && !byId.has(id)) {
        byId.set(id, {
          id,
          name: item.assigneeName || item.technicianName || "Intervenant",
        });
      }
    });

    return Array.from(byId.values()).sort((a, b) =>
      a.name.localeCompare(b.name, "fr")
    );
  }, [dashboard, weekItems]);

  const filteredItems = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase("fr-FR");

    return weekItems.filter((item) => {
      if (typeFilter !== "all") {
        const normalizedType = item.type === "inspection" ? "other" : item.type;
        if (normalizedType !== typeFilter) return false;
      }

      if (
        technicianFilter !== "all" &&
        (item.assignedUserId || item.assignedTechnicianId) !== technicianFilter
      ) {
        return false;
      }

      if (!needle) return true;

      return [
        typeMeta(item.type).label,
        item.machineName,
        item.machineSerialNumber,
        item.clientName,
        item.siteName,
        item.assigneeName,
        item.technicianName,
        statusMeta(item.status).label,
      ]
        .filter(Boolean)
        .join(" ")
        .toLocaleLowerCase("fr-FR")
        .includes(needle);
    });
  }, [search, technicianFilter, typeFilter, weekItems]);

  const itemsByDay = useMemo(() => {
    const map = new Map(days.map((day) => [dateKey(day), []]));

    filteredItems.forEach((item) => {
      const key = item.scheduledDate || dateKey(item.scheduledStart);
      if (map.has(key)) map.get(key).push(item);
    });

    map.forEach((items) => {
      items.sort(
        (a, b) =>
          new Date(a.scheduledStart).getTime() -
          new Date(b.scheduledStart).getTime()
      );
    });

    return map;
  }, [days, filteredItems]);

  const weeklyWorkload = useMemo(() => {
    const counts = new Map();

    weekItems.forEach((item) => {
      const id = item.assignedUserId || item.assignedTechnicianId;
      if (!id || item.status === "cancelled") return;
      const current = counts.get(id) || {
        technicianId: id,
        technicianName: item.technicianName || "Technicien",
        count: 0,
      };
      current.count += 1;
      counts.set(id, current);
    });

    safeArray(dashboard?.technicianWorkload).forEach((item) => {
      if (!counts.has(item.technicianId)) {
        counts.set(item.technicianId, {
          technicianId: item.technicianId,
          technicianName: item.technicianName || "Technicien",
          count: 0,
        });
      }
    });

    return Array.from(counts.values()).sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.technicianName.localeCompare(b.technicianName, "fr");
    });
  }, [dashboard, weekItems]);

  const removeCancelled = async (item) => {
    if (item.status !== "cancelled" || !["admin", "maintenance_manager"].includes(user?.role)) return;
    if (!window.confirm("Retirer cette intervention annulée du planning ? Son historique sera conservé.")) return;
    try {
      await gmao.hideCancelledInterventionFromPlanning(item.id);
      toast.success("Intervention retirée du planning.");
      await load();
    } catch (error) {
      toast.error(formatApiError(error?.response?.data?.detail) || "Impossible de retirer l’intervention.");
    }
  };

  const openItem = (item) => {
    navigate(interventionDestination(item, user));
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <section className="rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-heading text-sm font-black text-[var(--do-ink)]">
              Charge intervenants
            </div>
            <div className="mt-1 text-xs text-[var(--do-muted)]">
              Actions programmées sur la semaine affichée
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {weeklyWorkload.length ? (
              weeklyWorkload.map((item) => (
                <button
                  type="button"
                  key={item.technicianId}
                  onClick={() => setTechnicianFilter(item.technicianId)}
                  className="rounded-xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] px-3 py-2 text-left hover:border-[var(--do-accent)]"
                >
                  <div className="text-xs font-bold text-[var(--do-ink)]">
                    {item.technicianName}
                  </div>
                  <div className="text-[10px] text-[var(--do-muted)]">
                    {item.count} action{item.count > 1 ? "s" : ""}
                  </div>
                </button>
              ))
            ) : (
              <span className="text-xs text-[var(--do-muted)]">
                Aucune charge sur cette semaine.
              </span>
            )}
          </div>
        </div>
      </section>

      <section className="mt-4 flex flex-col gap-3 rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-4 lg:flex-row lg:items-center">
        <label className="flex min-w-0 flex-1 items-center gap-2 rounded-xl border border-[var(--do-border)] px-3 py-2">
          <MagnifyingGlass size={18} />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Rechercher machine, client, intervenant..."
            className="min-w-0 flex-1 bg-transparent text-sm outline-none"
          />
        </label>

        <select
          value={typeFilter}
          onChange={(event) => setTypeFilter(event.target.value)}
          className="rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-sm"
        >
          <option value="all">Tous les types</option>
          <option value="installation">Installation</option>
          <option value="preventive">Maintenance préventive</option>
          <option value="corrective">Panne / Réparation</option>
          <option value="replacement">Remplacement</option>
          <option value="other">Autre</option>
        </select>

        {user?.role !== "technician" ? (
          <select
            value={technicianFilter}
            onChange={(event) => setTechnicianFilter(event.target.value)}
            className="rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-sm"
          >
            <option value="all">Tous les intervenants</option>
            {technicians.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        ) : null}
      </section>

      <section className="mt-4 rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setWeekStart((current) => addDays(current, -7))}
              className="rounded-xl border border-[var(--do-border)] p-2"
              aria-label="Semaine précédente"
            >
              <CaretLeft size={18} weight="bold" />
            </button>
            <button
              type="button"
              onClick={() => setWeekStart(startOfWeek(new Date()))}
              className="rounded-xl border border-[var(--do-border)] px-3 py-2 text-xs font-bold"
            >
              Aujourd’hui
            </button>
            <button
              type="button"
              onClick={() => setWeekStart((current) => addDays(current, 7))}
              className="rounded-xl border border-[var(--do-border)] p-2"
              aria-label="Semaine suivante"
            >
              <CaretRight size={18} weight="bold" />
            </button>
          </div>

          <div className="flex items-center gap-2 text-sm font-heading font-black text-[var(--do-ink)]">
            <CalendarBlank size={18} weight="duotone" />
            {formatWeekRange(weekStart)}
          </div>
        </div>

        {loading ? (
          <div className="py-12 text-center text-sm text-[var(--do-muted)]">
            Chargement du planning…
          </div>
        ) : (
          <div className="mt-4 grid gap-3 xl:grid-cols-7">
            {days.map((day) => {
              const key = dateKey(day);
              const items = itemsByDay.get(key) || [];
              const today = key === dateKey(new Date());

              return (
                <section
                  key={key}
                  className="min-h-[210px] rounded-xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] p-2.5"
                >
                  <div
                    className={`mb-2 rounded-lg px-2 py-1.5 text-xs font-heading font-black ${
                      today
                        ? "bg-[var(--do-accent-soft)] text-[var(--do-accent)]"
                        : "text-[var(--do-ink)]"
                    }`}
                  >
                    {formatShortDate(day)}
                  </div>

                  <div className="space-y-2">
                    {items.length ? (
                      items.map((item) => (
                        <PlanningCard
                          key={item.id}
                          item={item}
                          onOpen={openItem}
                          onRemove={removeCancelled}
                          canRemove={["admin", "maintenance_manager"].includes(user?.role)}
                        />
                      ))
                    ) : (
                      <div className="rounded-lg border border-dashed border-[var(--do-border)] px-2 py-5 text-center text-[10px] text-[var(--do-muted)]">
                        Aucune action
                      </div>
                    )}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </section>

      <div className="mt-3 flex items-center gap-2 text-xs text-[var(--do-muted)]">
        <Wrench size={15} />
        Le planning affiche uniquement les actions GMAO déjà programmées. La modification se fait en ouvrant la fiche ; aucun glisser-déposer n’est utilisé.
      </div>
    </div>
  );
}
